import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  IconButton,
  TextField,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import { NoticeboardButton } from "../shared";
import { User } from "../../services/api";
import { lightTheme } from "../../styles/theme";
import { useBodyScrollLock } from "../../hooks/useBodyScrollLock";

/** Minimal shape for delete confirmation (matches `User` for school removal). */
export type DeleteConfirmationUser = Pick<User, "id" | "name">;

interface DeleteConfirmationDialogProps {
  user: DeleteConfirmationUser | null;
  onClose: () => void;
  onConfirm: (userId: string) => void;
  isRemoving: boolean;
  /**
   * When set (e.g. circle display name), copy explains removal from that context
   * instead of from the whole school.
   */
  removeFromContextName?: string;
}

const DeleteConfirmationDialog: React.FC<DeleteConfirmationDialogProps> =
  React.memo(({ user, onClose, onConfirm, isRemoving, removeFromContextName }) => {
    const [confirmationText, setConfirmationText] = useState("");
    useBodyScrollLock(Boolean(user));

    return (
      <Dialog
        key={user?.id ?? "closed"}
        open={!!user}
        disableScrollLock
        onClose={() => {
          if (isRemoving) return;
          onClose();
          setConfirmationText(""); // Clear confirmation text
        }}
        aria-labelledby="delete-dialog-title"
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: "hidden",
            maxHeight: "90vh",
            m: { xs: 1, sm: 4 },
            width: { xs: "calc(100% - 16px)", sm: "100%" },
          },
        }}
      >
        {/* Modern Header */}
        <Box
          sx={{
            background: `linear-gradient(135deg, ${lightTheme.colors.red} 0%, ${lightTheme.colors.red600} 100%)`,
            color: "common.white",
            p: 3,
            position: "relative",
          }}
        >
          <IconButton
            onClick={() => {
              if (isRemoving) return;
              onClose();
              setConfirmationText("");
            }}
            disabled={isRemoving}
            sx={{
              position: "absolute",
              top: 16,
              right: 16,
              color: "common.white",
              bgcolor: lightTheme.colors.headerBorder,
              "&:hover": {
                bgcolor: lightTheme.colors.headerButtonHover,
              },
            }}
          >
            <Close />
          </IconButton>

          <Typography variant="h5" fontWeight={600} sx={{ mb: 1, pr: 6 }}>
            Remove User
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.9 }}>
            This action cannot be undone
          </Typography>
        </Box>

        <DialogContent sx={{ p: 0, bgcolor: "grey.50" }}>
          <Box sx={{ p: 3 }}>
            <Typography
              variant="body1"
              sx={{ mb: 3, fontSize: "1rem", lineHeight: 1.6 }}
            >
              {removeFromContextName ? (
                <>
                  Are you sure you want to remove{" "}
                  <Typography component="span" fontWeight={600}>
                    {user?.name}
                  </Typography>{" "}
                  from{" "}
                  <Typography component="span" fontWeight={600}>
                    {removeFromContextName}
                  </Typography>
                  ? They will lose access to this circle&apos;s notices and
                  resources. This action cannot be undone.
                </>
              ) : (
                <>
                  Are you sure you want to remove{" "}
                  <Typography component="span" fontWeight={600}>
                    {user?.name}
                  </Typography>{" "}
                  from the school? This action cannot be undone and the user
                  will lose access to all school resources.
                </>
              )}
            </Typography>

            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                To confirm removal, please type the user's name:{" "}
                <Typography
                  component="span"
                  fontWeight={600}
                  color="error.main"
                >
                  {user?.name}
                </Typography>
              </Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="Type the user's name to confirm"
                value={confirmationText}
                onChange={(e) => setConfirmationText(e.target.value)}
                sx={{ mt: 1 }}
              />
            </Box>

            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
              <NoticeboardButton
                variant="danger"
                onClick={() => user && onConfirm(user.id)}
                disabled={isRemoving || confirmationText !== user?.name}
              >
                {isRemoving ? "Removing..." : "Remove User"}
              </NoticeboardButton>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>
    );
  });

export default DeleteConfirmationDialog;
